import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Timestamp,
  collection,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../../firebase";
import EXIF from "exif-js";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { format } from "date-fns";

const Field = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ mb: 0.5 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const compressImage = (file, maxWidth = 1200, targetSizeKB = 100) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = async () => {
      const targetBytes = targetSizeKB * 1024;

      const makeCanvas = (width) => {
        const scale = Math.min(width / img.width, 1);
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        return c;
      };

      const toBlob = (canvas, q) =>
        new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", q));

      const findBestBlob = async (canvas) => {
        const hiBlob = await toBlob(canvas, 0.92);
        if (hiBlob.size <= targetBytes) return hiBlob;
        let lo = 0.3,
          hi = 0.92,
          best = null;
        for (let i = 0; i < 8; i++) {
          const mid = (lo + hi) / 2;
          const blob = await toBlob(canvas, mid);
          if (blob.size <= targetBytes) {
            best = blob;
            lo = mid;
          } else hi = mid;
        }
        return best ?? (await toBlob(canvas, 0.3));
      };

      try {
        let blob = await findBestBlob(makeCanvas(maxWidth));
        if (blob.size > targetBytes) blob = await findBestBlob(makeCanvas(800));
        resolve(
          new File([blob], file.name, {
            type: "image/jpeg",
            lastModified: Date.now(),
          }),
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = reject;
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function DiaryForm({ open, onClose, entry, onDelete }) {
  const { childId } = useParams();
  const { t } = useTranslation();
  const isEdit = !!entry;

  const [text, setText] = useState("");
  const [datetimeStr, setDatetimeStr] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (open) {
      setText(entry?.text ?? "");
      setDatetimeStr(
        entry ? format(entry.createdAt, "yyyy-MM-dd'T'HH:mm") : "",
      );
      setImageFile(null);
      setPreviewUrl(entry?.imageUrl ?? null);
    }
  }, [open, entry]);

  const handleClose = () => {
    setText("");
    setDatetimeStr("");
    setImageFile(null);
    setPreviewUrl(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!childId || !text.trim()) return;

    if (isEdit) {
      const docRef = doc(db, `children/${childId}/diary/${entry.id}`);
      let updatedFields = { text };

      const newCreatedAt = new Date(datetimeStr);
      if (!isNaN(newCreatedAt)) {
        updatedFields.createdAt = Timestamp.fromDate(newCreatedAt);
      }

      // Image removed
      if (!previewUrl && !imageFile && entry.imageUrl) {
        try {
          await deleteObject(ref(storage, entry.imageUrl));
        } catch {}
        updatedFields.imageUrl = null;
      }

      // New image uploaded
      if (imageFile) {
        if (entry.imageUrl) {
          try {
            await deleteObject(ref(storage, entry.imageUrl));
          } catch {}
        }
        const compressed = await compressImage(imageFile);
        const imageRef = ref(
          storage,
          `children/${childId}/diaryImages/${Date.now()}_${imageFile.name}`,
        );
        const snapshot = await uploadBytes(imageRef, compressed);
        updatedFields.imageUrl = await getDownloadURL(snapshot.ref);
      }

      await updateDoc(docRef, updatedFields);
    } else {
      let imageUrl = null;
      let createdAt = Timestamp.now();

      EXIF.getData(imageFile, function () {
        const takenDate = EXIF.getTag(this, "DateTimeOriginal");
        if (takenDate) {
          const [datePart, timePart] = takenDate.split(" ");
          const [year, month, day] = datePart.split(":").map(Number);
          const [hour, minute, second] = timePart.split(":").map(Number);
          createdAt = Timestamp.fromDate(
            new Date(year, month - 1, day, hour, minute, second),
          );
        }
      });

      if (imageFile) {
        const compressed = await compressImage(imageFile);
        const imageRef = ref(
          storage,
          `children/${childId}/diaryImages/${Date.now()}_${imageFile.name}`,
        );
        const snapshot = await uploadBytes(imageRef, compressed);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      await addDoc(collection(db, `children/${childId}/diary`), {
        text,
        imageUrl,
        createdAt,
      });
    }

    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>
        {isEdit ? t("diary.edit_entry") : t("diary.new_entry")}
      </DialogTitle>
      <DialogContent dividers>
        {isEdit && (
          <Field label={t("edit.datetime")}>
            <TextField
              type="datetime-local"
              value={datetimeStr}
              onChange={(e) => setDatetimeStr(e.target.value)}
              fullWidth
            />
          </Field>
        )}

        <Field label={t("edit.photo")}>
          <input
            type="file"
            accept="image/*"
            id="diary-image-upload"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setImageFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }
            }}
          />
          {!previewUrl ? (
            <label htmlFor="diary-image-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                size="small"
              >
                {t("edit.choose_image")}
              </Button>
            </label>
          ) : (
            <Box>
              <label htmlFor="diary-image-upload">
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxHeight: 100, borderRadius: 8, cursor: "pointer" }}
                />
              </label>
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setImageFile(null);
                  setPreviewUrl(null);
                }}
                fullWidth
              >
                {t("edit.remove_image")}
              </Button>
            </Box>
          )}
        </Field>

        <Field label={t("diary.note")}>
          <TextField
            multiline
            minRows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
          />
        </Field>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        {isEdit ? (
          <Button
            color="error"
            onClick={onDelete}
          >
            {t("common.delete")}
          </Button>
        ) : (
          <Box />
        )}
        <Box>
          <Button onClick={handleClose}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!text.trim()}
            sx={{
              backgroundColor: darken("#7AA490", 0.2),
              "&:hover": { backgroundColor: darken("#7AA490", 0.3) },
              ml: 1,
            }}
          >
            {t("common.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
